import React, { useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import DataCard, { tableHeaderCellSx } from "../components/DataCard";
import usePaginatedList from "../hooks/usePaginatedList";
import { KV, DetailDialog, Pre, RequestLink } from "./ActivityParts";
import { fmtDateTime, fmtJson } from "./activityFilters";

const OUTCOMES = ["success", "failure", "denied"];
const OUTCOME_COLOR = { success: "success", failure: "error", denied: "warning" };

/** Audit trail tab: who did what to which entity, when, with what outcome.
 *  Server-paged + filtered by the shared applog.page_audit_events contract. */
export default function AuditTrailPanel({ requestId, since, onRequestId }) {
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [entityId, setEntityId] = useState("");
  const [outcomes, setOutcomes] = useState(OUTCOMES);
  const [selected, setSelected] = useState(null);

  const { records, total, loading, error, page, setPage, pageSize, setPageSize, q, setQ } =
    usePaginatedList("/audit-event-list", {
      initialPageSize: 50,
      initialSort: "ts",
      initialOrder: "desc",
      extraParams: {
        action: action.trim(),
        actor: actor.trim(),
        entity_id: entityId.trim(),
        // all selected = no outcome filter; the server splits a comma list
        outcome: outcomes.length === OUTCOMES.length ? "" : outcomes.join(","),
        request_id: requestId,
        since,
      },
    });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
        <TextField size="small" label="Search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="summary / action / entity / actor / details" sx={{ minWidth: 280, flex: 1 }} />
        <TextField size="small" label="Action" value={action} onChange={(e) => setAction(e.target.value)}
                   placeholder="create_run or area.*" />
        <TextField size="small" label="Actor" value={actor} onChange={(e) => setActor(e.target.value)} />
        <TextField size="small" label="Entity id" value={entityId} onChange={(e) => setEntityId(e.target.value)} />
        <ToggleButtonGroup
          size="small"
          value={outcomes}
          onChange={(_, v) => setOutcomes(v.length ? v : OUTCOMES)}
          aria-label="Outcome"
        >
          {OUTCOMES.map((o) => (
            <ToggleButton key={o} value={o} title={`Toggle ${o}`}>{o}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error">Failed to load the audit trail. Please try again.</Alert>}

      <DataCard noPadding>
        {loading && records.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Time", "Actor", "Action", "Entity", "Summary", "Outcome", "Req"].map((h) => (
                    <TableCell key={h} sx={tableHeaderCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No matching audit events.
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((r) => (
                    <TableRow key={r.id} hover onClick={() => setSelected(r)} sx={{ cursor: "pointer" }}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDateTime(r.ts)}</TableCell>
                      <TableCell>{r.actor_email || r.actor_user_id || r.source || "system"}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>{r.action}</TableCell>
                      <TableCell>
                        {r.entity_type || "·"}
                        {r.entity_id ? <span style={{ fontFamily: "monospace", opacity: 0.7 }}> {r.entity_id}</span> : null}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>{r.summary || ""}</TableCell>
                      <TableCell>
                        <Chip size="small" label={r.outcome || "success"} color={OUTCOME_COLOR[r.outcome] || "success"} variant="outlined" />
                      </TableCell>
                      <TableCell><RequestLink id={r.request_id} onRequestId={onRequestId} /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </TableContainer>
        )}
      </DataCard>

      {selected && (
        <DetailDialog
          title={<><Chip size="small" label={selected.outcome || "success"} color={OUTCOME_COLOR[selected.outcome] || "success"} />
                   <span style={{ fontFamily: "monospace" }}>{selected.action}</span></>}
          onClose={() => setSelected(null)}
          actions={[
            ...(selected.request_id
              ? [{ label: "Show everything from this request", onClick: () => { onRequestId(selected.request_id); setSelected(null); } }]
              : []),
            ...(selected.entity_id && selected.entity_id !== entityId
              ? [{ label: `History of this ${selected.entity_type || "entity"}`, onClick: () => { setEntityId(selected.entity_id); setSelected(null); } }]
              : []),
          ]}
        >
          <KV k="Time" v={selected.ts} />
          <KV k="Actor" v={selected.actor_email || selected.actor_user_id} />
          <KV k="Source" v={selected.source} />
          <KV k="Entity" v={[selected.entity_type, selected.entity_id].filter(Boolean).join(" ")} />
          <KV k="Parent" v={selected.parent_entity_id} />
          <KV k="Status code" v={selected.status_code} />
          <KV k="Request" v={selected.request_id} />
          <KV k="Summary" v={selected.summary} />
          {selected.details != null && <Box sx={{ pt: 1 }}><Pre>{fmtJson(selected.details)}</Pre></Box>}
        </DetailDialog>
      )}
    </Box>
  );
}
